import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-tag-css.js';

export const tagColors = ['blue', 'green', 'aqua', 'purple', 'rose', 'red', 'orange', 'yellow', 'gray'] as const;

/**
 * Tags are used to highlight, categorize, or organize items.
 *
 * @element zui-tag
 *
 * @slot - Default, unnamed slot; for inserting label text into `<zui-tag>`
 *
 * @prop {string} color - property to display the color of the tag (required)
 */
export class ZuiTagElement extends ZuiBaseElement {
  /**
   * property to display the color of the tag (optional, but doesn't show)
   */
  @property({ type: String })
  get color() {
    return this.#color;
  }
  set color(color: TagColor | '') {
    const oldColor = this.#color;
    this.#color = this.#isValidColor(color) ? color : '';
    this.requestUpdate('color', oldColor);
  }

  #color: TagColor | '' = '';

  static get styles() {
    return [super.styles, style];
  }

  render() {
    const classes = { 'is-visible': this.color !== '' && this.#isValidColor(this.color) };
    let textColor = `--zui-${this.color}-700`;
    if (this.color === 'yellow') {
      textColor = '--zui-orange-700';
    }

    return html`<div
      class="tag ${classMap(classes)}"
      style="background:var(--zui-${this.color}-100); color:var(${textColor});"
      ><slot></slot
    ></div>`;
  }

  #isValidColor(c: TagColor | '') {
    if (c === '') {
      return false;
    }

    return tagColors.includes(c);
  }
}

window.customElements.define('zui-tag', ZuiTagElement);

export type TagColor = (typeof tagColors)[number];

declare global {
  interface HTMLElementTagNameMap {
    'zui-tag': ZuiTagElement;
  }
}
